import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import GenericTable from "@components/tables/GenericTable";
import { setSelectedAssignedFolderId } from "@store/slices/validatorSlice";
import { fetchValidatorFolders, fetchValidatorFiles } from "@store/thunks/validatorThunk";
import { restrictSpecialCharacters, ROLE_ROUTES } from "@utils/constants";
import { toast } from "react-toastify";
import { useDebounce } from "react-use";

const FolderDetailTableValidator = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [error, setError] = useState(null);
  const [inputValue, setInputValue] = useState("");
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState("");
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(true); // 👈 track if more pages exist

  const { selectedAssignedProjectId, assignedFolders = {} } = useSelector((store) => store.validator);
  const [popup, setPopup] = useState({ isOpen: false, type: "", message: "" });

  // Debounce search query
  useDebounce(
    () => {
      setDebouncedSearchQuery(inputValue.toLowerCase());
      setPageIndex(0);
    },
    300,
    [inputValue]
  );

  const handleInputChange = (e) => {
     const newValue = e.target.value;
    if (restrictSpecialCharacters(newValue) || newValue === "") {
      setInputValue(newValue);
      setPageIndex(0);
      setHasNextPage(true); // Reset search → assume more pages exist
    } else {
      setPopup({
        isOpen: true,
        type: "error",
        message: "Special characters are not allowed. Use only letters, numbers, and spaces.",
      });
    }
  };

  const handlePopupClose = () => {
    setPopup({ isOpen: false, type: "", message: "" });
  };

  useEffect(() => {
    const fetchFolders = async () => {
      if (!selectedAssignedProjectId) {
        setInitialLoading(false);
        return;
      }
      try {
        setInitialLoading(true);
        const res = await dispatch(
          fetchValidatorFolders(selectedAssignedProjectId, debouncedSearchQuery, pageIndex, pageSize)
        );
        if (Array.isArray(res)) {
          if (res.length < pageSize) {
            setHasNextPage(false);
          } else {
            setHasNextPage(true);
          }
        }
      } catch (error) {
        console.error("[FolderDetailTableValidator] Error fetching folders:", error);
        setError("Failed to fetch folders. Please try again.");
        toast.error("Failed to fetch folders.");
      } finally {
        setInitialLoading(false);
      }
    };

    fetchFolders();
  }, [dispatch, selectedAssignedProjectId, debouncedSearchQuery, pageIndex, pageSize]);

  const currentFolders = Array.isArray(assignedFolders[selectedAssignedProjectId])
    ? assignedFolders[selectedAssignedProjectId]
    : [];

  const handleFolderClick = async ({ row }) => {
    try {
      setLoading(true);
      const folderId = row.original.folderId;
      if (!folderId) {
        throw new Error("Invalid folder ID");
      }
      dispatch(setSelectedAssignedFolderId(folderId));
      await dispatch(fetchValidatorFiles(folderId, "", 0, 10));
      navigate(`${ROLE_ROUTES.VALIDATOR}/folders/files`);
    } catch (error) {
      console.error("[FolderDetailTableValidator] Error handling folder click:", error);
      setError("Failed to fetch files for the selected folder.");
      toast.error("Failed to fetch files for the selected folder.");
    } finally {
      setLoading(false);
    }
  };

  const folderColumns = [
    {
      id: "serial_number",
      header: "Sr. No.",
      cell: ({ row, table }) => {
        return pageIndex * pageSize + row.index + 1;
      },
      cellClassName: "text-center",
    },
    {
      id: "folderName",
      header: "Folder Name",
      accessorKey: "folderName",
      cell: ({ row }) => (
        <button
          onClick={() => handleFolderClick({ row })}
          className="text-[#ec7026] hover:text-[#d85d1a] hover:underline font-['Semantic/Strong']"
        >
          {row.original.folderName || "N/A"}
        </button>
      ),
    },
    {
      header: 'File Count',
      accessorKey: "fileCount",
      cell: ({ row }) => {
        console.log(row)
        const fileCount = row.original?.fileCount;
        return (
          <div className="text-center">
            {fileCount}
          </div>
        );
      },
      cellClassName: "text-center",
    },
    {
      id: "createdAt",
      header: "Created At",
      accessorKey: "createdAt",
      cell: ({ row }) => {
        const deadline = row.original?.createdAt;
        if (!deadline) return "N/A";
        try {
          const date = new Date(deadline);
          const now = new Date();
          const isOverdue = date < now;
          const isApproaching = !isOverdue && date - now < 7 * 24 * 60 * 60 * 1000;
          return (
            <div className="text-center">
              <div
                className={
                  isOverdue ? "text-red-600" : isApproaching ? "text-amber-600" : "text-gray-600"
                }
              >
                {date.toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </div>
              <div className="text-sm text-gray-500">
                {date.toLocaleTimeString("en-US", {
                  hour: "numeric",
                  minute: "numeric",
                  hour12: true,
                })}
              </div>
            </div>
          );
        } catch (e) {
          return deadline;
        }
      },
    },
  ];

  return (
    <div className="flex flex-col min-h-[calc(100vh-4rem)] bg-gray-50/50">
      <div className="lg:pr-8 lg:pt-8 flex justify-end items-center mb-6">
        <div className="relative w-full sm:w-64">
          <input
            type="text"
            placeholder="Search Folders..."
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#ec7026] text-base font-['Semantic/Strong'] text-gray-700 pl-10"
            value={inputValue}
            onChange={handleInputChange}
          />
          <svg
            className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
            />
          </svg>
        </div>
      </div>
      {popup.isOpen && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50 z-50">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 max-w-md w-full mx-4">
            <h2 className="text-lg font-['Semantic/Strong'] font-bold text-gray-900">
              {popup.type === "error" ? "Error" : "Info"}
            </h2>
            <p className="mt-2 text-sm text-gray-500 font-['Semantic/Strong']">
              {popup.message}
            </p>
            <div className="mt-4 flex justify-end">
              <button
                onClick={handlePopupClose}
                className="inline-flex items-center px-4 py-2 border border-[#ec7026] shadow-sm text-sm font-['Semantic/Strong'] rounded-lg text-white bg-[#ec7026] hover:bg-[#d85d1a] focus:outline-none focus:ring-2 focus:ring-[#ec7026]"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
      <div className="w-full p-4 sm:p-6 lg:p-8">
        {initialLoading || loading ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="animate-pulse space-y-4">
              <div className="h-8 bg-gray-200 rounded w-full"></div>
              <div className="h-8 bg-gray-200 rounded w-full"></div>
              <div className="h-8 bg-gray-200 rounded w-full"></div>
            </div>
          </div>
        ) : error ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
              <p className="font-['Semantic/Strong']">{error}</p>
              <p className="font-['Semantic/Strong'] mt-2">Please try again later or contact support.</p>
            </div>
          </div>
        ) : !selectedAssignedProjectId ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="text-gray-500 text-sm font-['Semantic/Strong']">
              Please select a project to view folders.
            </div>
          </div>
        ) : currentFolders.length === 0 ? (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded-lg">
              <p className="font-['Semantic/Strong']">No folders found.</p>
              <p className="font-['Semantic/Strong'] mt-2">
                You have not been assigned any folders. Please contact the administrator.
              </p>
            </div>
          </div>
        ) : (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
            <div className="w-full overflow-x-auto">
              <GenericTable
                data={[...currentFolders].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))}
                columns={folderColumns}
                showPagination={true}
                manualPagination={true}
                pageIndex={pageIndex}
                pageSize={pageSize}
                hasNextPage={hasNextPage}
                onPaginationChange={({ pageIndex, pageSize }) => {
                  setPageIndex(pageIndex);
                  setPageSize(pageSize);
                }}
                emptyMessage="No folders available for this project"
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default FolderDetailTableValidator;